export const ELIGIBLE_CATEGORY_TYPES = [1, 4, 5];

export const CATEGORY_TYPE_LABELS = {
  1: "Qwik",
  4: "UPI",
  5: "Hubble",
};

export function getVoucherCategoryType(voucher) {
  if (!voucher) return null;
  const type = voucher.category_type ?? voucher.category ?? voucher.select;
  return type != null ? Number(type) : null;
}

export function getCategoryTypeLabel(categoryType) {
  return CATEGORY_TYPE_LABELS[Number(categoryType)] || `Type ${categoryType}`;
}

export function isEligibleCategoryType(categoryType) {
  return ELIGIBLE_CATEGORY_TYPES.includes(Number(categoryType));
}

export function formatVoucherOptionLabel(voucher) {
  const cat = getCategoryTypeLabel(getVoucherCategoryType(voucher));
  return `${voucher.sku} — ${voucher.name} (${cat})`;
}

export async function fetchEligibleVouchers(axiosClient, limit = 500) {
  const responses = await Promise.all(
    ELIGIBLE_CATEGORY_TYPES.map((categoryType) =>
      axiosClient
        .get(`/admin/qwik-voucehrs/0/${limit}/false/${categoryType}`)
        .then((res) => {
          if (res.data.status_code === 401) localStorage.clear();
          const data = res.data.response || res.data.data || [];
          const list = Array.isArray(data) ? data : [];
          return list.map((v) => ({
            ...v,
            category_type: getVoucherCategoryType(v) || categoryType,
          }));
        })
        .catch(() => [])
    )
  );

  const bySku = {};
  responses.flat().forEach((v) => {
    if (!v.sku) return;
    const cat = getVoucherCategoryType(v);
    if (isEligibleCategoryType(cat)) {
      bySku[v.sku] = v;
    }
  });

  return Object.values(bySku).sort((a, b) => a.sku.localeCompare(b.sku));
}

export async function fetchSkuDenominations(axiosClient, sku) {
  const res = await axiosClient.get(
    `/admin/discount/sku-denominations?sku=${encodeURIComponent(sku)}`
  );
  if (res.data.status_code === 401) localStorage.clear();
  if (!res.data.status) {
    throw new Error(res.data.message || "Failed to load denominations");
  }
  return res.data.response;
}

export function buildDenominationRows(apiDenominations, savedDenominations = []) {
  const savedMap = {};
  (savedDenominations || []).forEach((d) => {
    savedMap[Number(d.denomination)] = d;
  });

  return (apiDenominations || []).map((denom) => {
    const denomination = Number(denom);
    const saved = savedMap[denomination];
    return {
      denomination,
      discount_percent: saved?.discount_percent ?? 0,
      max_discount_percent: saved?.max_discount_percent ?? 0,
    };
  });
}

export function formatDenominationsSummary(denominations) {
  if (!denominations?.length) return "—";
  if (denominations.length > 3) {
    return `${denominations.length} configured`;
  }
  return denominations
    .map((d) => `₹${d.denomination}: ${d.discount_percent}%`)
    .join(" / ");
}

export function validateDiscountForm(values, allowedDenominations, selectedVoucher) {
  const errors = {};
  const denominations = values.denominations || [];
  const cashbackPercent = values.cashback_percent;
  const maxDiscountCap = Number(values.max_discount_cap) || 0;

  if (selectedVoucher) {
    const categoryType = getVoucherCategoryType(selectedVoucher);
    if (!isEligibleCategoryType(categoryType)) {
      errors.sku =
        "Voucher category is not discount-eligible (allowed: Qwik, UPI, Hubble)";
    }
  }

  if (maxDiscountCap < 0) {
    errors.max_discount_cap = "Max discount cap must be >= 0";
  }

  if (cashbackPercent !== "" && cashbackPercent != null && Number(cashbackPercent) < 0) {
    errors.cashback_percent = "Cashback percent must be >= 0";
  }

  const allowedSet = new Set((allowedDenominations || []).map(Number));
  const seen = new Set();

  if (values.discount_enabled && denominations.length === 0) {
    errors.denominations = "At least one denomination is required when discount is enabled";
    return errors;
  }

  denominations.forEach((row, index) => {
    const denom = Number(row.denomination);
    const discountPercent = Number(row.discount_percent);
    const maxDiscountPercent = Number(row.max_discount_percent);
    const prefix = `denominations.${index}`;

    if (seen.has(denom)) {
      errors[`${prefix}.denomination`] = `Duplicate denomination ${denom}`;
    }
    seen.add(denom);

    if (allowedSet.size > 0 && !allowedSet.has(denom)) {
      errors[`${prefix}.denomination`] = `Denomination ${denom} is not valid for this voucher`;
    }

    if (discountPercent < 0 || discountPercent > 100) {
      errors[`${prefix}.discount_percent`] = "Must be between 0 and 100";
    }
    if (maxDiscountPercent < 0 || maxDiscountPercent > 100) {
      errors[`${prefix}.max_discount_percent`] = "Must be between 0 and 100";
    }
    if (discountPercent > maxDiscountPercent) {
      errors[`${prefix}.discount_percent`] = "Cannot exceed max discount %";
    }
    if (values.discount_enabled && maxDiscountPercent <= 0) {
      errors[`${prefix}.max_discount_percent`] =
        "Must be greater than 0 when discount is enabled";
    }
  });

  return errors;
}

export function buildDiscountPayload(values, isEdit = false) {
  const payload = {
    discount_enabled: Boolean(values.discount_enabled),
    discount_type: values.discount_type || "upto",
    denominations: (values.denominations || []).map((row) => ({
      denomination: Number(row.denomination),
      discount_percent: Number(row.discount_percent) || 0,
      max_discount_percent: Number(row.max_discount_percent) || 0,
    })),
    max_discount_cap: Number(values.max_discount_cap) || 0,
    cashback_percent:
      values.cashback_percent === "" || values.cashback_percent == null
        ? null
        : Number(values.cashback_percent),
  };

  if (!isEdit) {
    payload.publisher_id = values.publisher_id;
    payload.sku = values.sku;
  }

  return payload;
}

export function syncDenominationRowForType(row, discountType) {
  if (discountType === "fixed") {
    return { ...row, max_discount_percent: row.discount_percent };
  }
  return row;
}

// API sends IST datetime with a trailing Z — display as-is, no timezone shift
export function formatToIST(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return date.toLocaleString("en-IN", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export function getDenominationFieldError(errors, index, field) {
  return errors[`denominations.${index}.${field}`];
}
